// Phase 5d — shared machinery for the data-table report tabs.
//
// Three things live here, all ported from the legacy report tabs:
//
//  1. The generated-report cache: the legacy views stash results on
//     `this.data[state]` where `state = current_report + project_id + ahu_id`
//     (e.g. phx_selection.js:180,205 / ahu_model.js:68-79). The cache is what
//     makes the re-open path work: reportIsGenerated (action.js:240-249)
//     triggers 'open:generated_report:<report>' and the tabs whose handler is
//     LIVE (phx_selection.js:166-174, ahu_model.js:83-92) re-render from the
//     cache and flip `generated = true`, which removes the empty board
//     (action.js:60-64). Session-memory only, exactly like the Backbone
//     views' `this.data`.
//
//  2. The numeric formatter (fn.uni_tools.bindFormatter →
//     universal_fn/formatter.js): decode (:101-126) strips a value to
//     digits/'.'/'-' and parseFloats it; encode (:127-180) comma-groups the
//     integer part and rounds decimals to 2dp / floats to 4dp (integers keep
//     NO fraction — truncation, not rounding). Span semantics (:251-269)
//     leave falsy text untouched; input semantics (:227-243) turn empty into
//     ''. The NaN quirk is preserved: whitespace-only text decodes to NaN and
//     encodes to the literal string 'NaN' (decode keeps '\n' because
//     isNaN('\n') is false, then parseFloat('\n') is NaN).
//
//  3. Handlebars-semantics helpers for the ported templates: `hbIf` is the
//     Handlebars truthiness table (0 truthy, '' / null / [] falsy), `hbStr`
//     the mustache stringification (missing → ''), `ifCompare` the report
//     board's helper (report/libs/handlebars/helpers.js:6-57).

import type { SocketDataMessage } from 'app/socketClient';

// ---- state keys + cache ----

export interface GenerateStateKeyFields {
  current_report: string;
  project_id: string;
  ahu_id: string;
}

/** `data.current_report + data.project_id + data.ahu_id` — the exact legacy
 *  concatenation (phx_selection.js:180, ahu_model.js:68, action.js:242-243
 *  '0'-defaults both ids before the check). */
export function generateStateKey(state: GenerateStateKeyFields): string {
  return state.current_report + state.project_id + state.ahu_id;
}

/** 5e: the order tabs key by report + project ONLY — `data.current_report +
 *  data.project_id`, no ahu segment (sales_order.js:88 generate / :75 reopen,
 *  service_order.js:79/:66, shipping_order.js:123/:111) — so a reopen with a
 *  DIFFERENT single AHU selected still hits the cache, unlike the 3-part key.
 *  5f: submittal_package uses the same 2-part key (submittal.package.js:437
 *  socket write / :377 reopen check). */
const PROJECT_KEYED_REPORTS = new Set([
  'sales_order',
  'service_order',
  'shipping_order',
  'submittal_package',
]);

/** The per-report cache key: 2-part for the order family, the standard 3-part
 *  generateStateKey for everything else. Store (generateReport.ts dispatch)
 *  and reopen lookup (ReportsBoardPage evalReopenKey) both use this. */
export function reportCacheKey(state: GenerateStateKeyFields): string {
  if (PROJECT_KEYED_REPORTS.has(state.current_report)) {
    return state.current_report + state.project_id;
  }
  return generateStateKey(state);
}

/** One cached generated-report result (the legacy `this.data[state]`). */
export interface CachedGeneratedReport {
  /** phx_selection: the full socket message (data.phx_selection_data). */
  message?: SocketDataMessage;
  /** ahu_model: the fetched collection rows (collection.toJSON());
   *  fan_data: the fan_selection {fan_data:true} response rows — the multi-AHU
   *  chain overwrites this key per AHU, reproducing the legacy
   *  render-replaces-render sequence (fan_data.js:197). */
  models?: Array<Record<string, unknown>>;
  /** generetionTimeStamp() taken when the result arrived. */
  timeStamp: string;
}

const cache = new Map<string, CachedGeneratedReport>();
const listeners = new Set<() => void>();

export function storeGeneratedReport(key: string, entry: CachedGeneratedReport): void {
  cache.set(key, entry);
  listeners.forEach((l) => l());
}

export function readGeneratedReport(key: string): CachedGeneratedReport | undefined {
  return cache.get(key);
}

export function hasGeneratedReport(key: string): boolean {
  return cache.has(key);
}

/** Subscribe to cache writes (the panes wait for the phx socket result). */
export function subscribeGeneratedReports(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// ---- fn.uni_tools.bindFormatter port (universal_fn/formatter.js) ----

export type FormatterMode = 'integer' | 'decimal' | 'float';

/** BaseNumericFormatter.decode (formatter.js:101-126). Returns undefined for
 *  '' (legacy returns nothing), NaN for whitespace-only quirk input. */
export function decodeNumeric(raw: unknown): number | undefined {
  const encodedValue = (raw == null ? '' : String(raw));
  if (encodedValue === '') return undefined;
  let res = '';
  let numbersStarted = false;
  for (const c of encodedValue) {
    // legacy: (!isNaN(c) && c != ' ') || c == '.' || c == '-'
    if ((!Number.isNaN(Number(c)) && c !== ' ') || c === '.' || c === '-') {
      if (!numbersStarted && c && !Number.isNaN(Number(c))) numbersStarted = true;
      if (c === '0' && !numbersStarted) continue;
      if (c === '-' && res) continue;
      res += c;
    }
  }
  if (!res || res === '.' || res === '-') return 0;
  return parseFloat(res);
}

/** BaseNumericFormatter.encode with round=true (formatter.js:127-180). */
export function encodeNumeric(decoded: number | undefined, mode: FormatterMode): string {
  if (decoded == null) return ''; // :132
  let isNegative = decoded < 0;
  const abs = isNegative ? -decoded : decoded;
  const value = String(abs);
  const parts = value.split('.');
  let leftPart = parts[0]
    .split('')
    .reverse()
    .map((e, i, symbols) => ((i + 1) % 3 === 0 && i !== symbols.length - 1 ? `,${e}` : e))
    .reverse()
    .join('');
  let rightPart = '';
  const num = Number(abs);
  if (mode === 'decimal' && !Number.isNaN(num)) {
    rightPart = num.toFixed(2).split('.')[1];
  } else if (mode === 'float' && !Number.isNaN(num)) {
    rightPart = num.toFixed(4).split('.')[1];
  }
  if (!leftPart) {
    if (rightPart) leftPart = '0';
    else isNegative = false;
  }
  return (isNegative ? '-' : '') + leftPart + (rightPart ? `.${rightPart}` : '');
}

/** NumericSpanFormatter.setValue (formatter.js:251-269): falsy text is left
 *  exactly as-is (so an empty interpolation stays empty). */
export function formatSpanValue(raw: unknown, mode: FormatterMode): string {
  const text = raw == null ? '' : String(raw);
  if (!text) return text;
  const decoded = decodeNumeric(text);
  // text is non-empty so decoded is a number (possibly NaN → 'NaN', the
  // legacy whitespace quirk).
  return encodeNumeric(decoded as number, mode);
}

/** NumericInputFormatter.setValue (formatter.js:227-243): empty stays ''. */
export function formatInputValue(raw: unknown, mode: FormatterMode): string {
  const text = raw == null ? '' : String(raw);
  const decoded = decodeNumeric(text);
  if (decoded === undefined) return '';
  return encodeNumeric(decoded, mode);
}

// ---- Handlebars semantics ----

/** Handlebars `{{#if}}` truthiness: 0 is TRUTHY, ''/null/undefined/false and
 *  empty arrays are falsy (Handlebars Utils.isEmpty). */
export function hbIf(v: unknown): boolean {
  if (!v && v !== 0) return false;
  if (Array.isArray(v) && v.length === 0) return false;
  return true;
}

/** `{{value}}` stringification: missing/null → ''. */
export function hbStr(v: unknown): string {
  return v == null ? '' : String(v);
}

/** report/libs/handlebars/helpers.js ifCompare — the operators used by the
 *  5d templates ('==', '!=', '<=', '>'). Loose comparison like the helper. */
export function ifCompare(l: unknown, operator: '==' | '!=' | '<=' | '>', r: unknown): boolean {
  switch (operator) {
    case '==':
      // eslint-disable-next-line eqeqeq
      return (l as never) == (r as never);
    case '!=':
      // eslint-disable-next-line eqeqeq
      return (l as never) != (r as never);
    case '<=':
      return (l as never) <= (r as never);
    case '>':
      return (l as never) > (r as never);
    default:
      return false;
  }
}

// ---- Backbone collection fetch equivalent ----

/** Backbone collection.fetch() GET — same-origin + XHR header. Throws on
 *  !ok, matching the legacy `.then` that simply never runs on failure. */
export async function fetchCollection<T>(url: string): Promise<T> {
  const resp = await fetch(url, {
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  });
  if (!resp.ok) {
    throw new Error(`fetchCollection ${url} failed: ${resp.status}`);
  }
  return (await resp.json()) as T;
}
